import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, createAction, Store } from '@ngrx/store';
import { DateTime } from 'luxon';
import { of, timer } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { NotificationsAdapter } from '../../adapters/notifications.adapter';
import { AppState } from '../../app.reducer';
import { selectLoggedIn } from '../auth/auth.reducer';
import {
  loadNotificationsFailure,
  loadNotificationsLazy,
  loadNotificationsLazySuccess,
  loadNotificationsPolling,
  loadNotificationsPollingSuccess,
  markAllNotificationsAsSeen,
  markAllNotificationsAsSeenFailure,
  markAllNotificationsAsSeenSuccess,
  toggleNotificationState,
  toggleNotificationStateSuccess,
} from './notifications.actions';
import { selectNotificationsLazyParameters, selectNotificationsPollingParameters } from './notifications.reducer';

export const NotificationsEffectInit = createAction('[Notification/Effects] Notification Effect Initialized');

@UntilDestroy()
@Injectable()
export class NotificationsEffects implements OnInitEffects {
  private readonly actions$ = inject(Actions);
  private readonly notificationsAdapter = inject(NotificationsAdapter);
  private readonly store = inject(Store<AppState>);
  
  private readonly INTERVAL: number = 30000;

  ngrxOnInitEffects(): Action {
    timer(0, this.INTERVAL)
      .pipe(
        concatLatestFrom(() => this.store.select(selectLoggedIn)),
        untilDestroyed(this)
      )
      .subscribe(([, loggedIn]) => {
        if (loggedIn) {
          this.store.dispatch(loadNotificationsPolling());
        }
      });
    return NotificationsEffectInit();
  }

  loadNotificationsLazy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadNotificationsLazy),
      concatLatestFrom(() => this.store.select(selectNotificationsLazyParameters)),
      switchMap(([, parameters]) => this.notificationsAdapter.getNotifications(parameters.offset, parameters.limit)),
      map((response) => loadNotificationsLazySuccess({ ...response, lastPoll: DateTime.now() })),
      catchError((error) => {
        return of(loadNotificationsFailure({ error }));
      })
    )
  );

  loadNotificationsPolling$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadNotificationsPolling),
      concatLatestFrom(() => this.store.select(selectNotificationsPollingParameters)),
      switchMap(([, parameters]) =>
        this.notificationsAdapter.getNotifications(parameters.offset, parameters.limit, parameters.lastPoll)
      ),
      map((response) => loadNotificationsPollingSuccess({ ...response, lastPoll: DateTime.now() })),
      catchError((error) => {
        return of(loadNotificationsFailure({ error }));
      })
    )
  );

  toggleNotificationState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleNotificationState),
      mergeMap((props) =>
        this.notificationsAdapter.toggleState(props.notification).pipe(
          map(() => toggleNotificationStateSuccess({ notification: props.notification })),
          catchError((error) => {
            return of(loadNotificationsFailure({ error }));
          })
        )
      )
    )
  );

  loadNotificationsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadNotificationsFailure),
        tap(() => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        })
      ),
    { dispatch: false }
  );

  markAllNotificationsAsSeen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(markAllNotificationsAsSeen),
      mergeMap(() =>
        this.notificationsAdapter.markAllAsSeen().pipe(
          map(() => markAllNotificationsAsSeenSuccess()),
          catchError((error) => {
            return of(markAllNotificationsAsSeenFailure({ error }));
          })
        )
      )
    )
  );
}
